import styled, { css } from 'styled-components';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';

const productInfoOverlayStyles = css`
  display: inline-block;
  margin-right: 8px;
`;

const productInfoMobileStyles = css`
  display: inline-block;
  margin-right: 8px;
`;

export const ProductInfo = styled.div<{
  isOverlay: boolean;
  hasLongPrice?: boolean;
}>`
  font-style: normal;
  font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['200']};
  margin-bottom: 4px;

  ${({ isOverlay, hasLongPrice }) => isOverlay && !hasLongPrice && productInfoOverlayStyles};

  @media (max-width: 767px) {
    ${({ hasLongPrice }) => !hasLongPrice && productInfoMobileStyles}
  }
`;

export const PriceTotalAmount = styled.div<{ isOverlay?: boolean }>`
  flex-basis: 100%;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  text-align: right;

  ${({ isOverlay }) =>
    isOverlay &&
    css`
      text-align: left;
    `};

  @media (max-width: 767px) {
    text-align: left;
  }
`;

export const PriceUnit = styled.div<{
  promotionType: PROMOTION_TYPES | null;
  isOverlay: boolean;
  isWhiteLabel: boolean;
  lastMinuteDeals: boolean;
}>`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  display: inline-block;
  font-weight: bold;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ isOverlay }) =>
    isOverlay &&
    css`
      display: inline;
    `};
`;

export const PriceDiscount = styled.span<{
  promotionType: PROMOTION_TYPES | null;
  isWhiteLabel?: boolean;
  lastMinuteDeals: boolean;
}>`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  font-style: normal;
  display: inline-block;
  height: 20px;
  text-decoration-line: line-through;
  text-decoration-style: solid;
  text-decoration-color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  padding: 0;
  text-decoration-thickness: 1px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};

  svg {
    margin-right: 2px;
    margin-bottom: 4px;
    vertical-align: middle;
    pointer-events: none;
    position: relative;
    z-index: 1;
  }
`;

export const myPriceDiscountPercentageStyles = css`
  background-color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[100]};
  color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[700]};
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
`;

export const PriceDiscountPercentage = styled.div<{ isOverlay: boolean; isMobile: boolean }>`
  padding: 4px 8px;
  text-align: center;
  vertical-align: middle;
  border-radius: 4px;
  ${myPriceDiscountPercentageStyles};
  margin-top: ${({ isOverlay, isMobile }) => (isOverlay || isMobile ? 0 : `4px`)};
`;

const priceAmountOverlay = css`
  text-align: right;
`;

export const PriceAmount = styled.span<{
  promotionType: PROMOTION_TYPES | null;
  isWhiteLabel?: boolean;
  isOverlay?: boolean;
  lastMinuteDeals: boolean;
}>`
  display: inline-block;
  ${({ theme }) => theme.kallisto.mixins.font('Title3')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  line-height: 24px;
  white-space: nowrap;

  ${({ isOverlay }) => isOverlay && priceAmountOverlay}
`;

// Only the amount is unbreakable. The label wraps under it rather than pushing the row out of the
// rail, which a long translation of pp/total does in the 312px map overlay.
export const PriceAmountGroup = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: inherit;
`;

// The row wraps on its own: the browser knows both the rail's width and the text's, which no
// character-count heuristic can. PriceAmountGroup keeps the unit welded to its amount.
// justify-content places the items, text-align the lines a label wraps onto — the rail needs both.
export const PriceDetailsContainer = styled.div<{ isOverlay?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-end;
  text-align: right;
  column-gap: 8px;
  row-gap: 0;

  ${({ isOverlay }) =>
    isOverlay &&
    css`
      justify-content: flex-start;
      text-align: left;
    `};

  @media (max-width: 767px) {
    justify-content: flex-start;
    text-align: left;
  }
`;
